import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { roastedStock, greenCategories } from '../lib/api';
import { Coffee, Truck, Users, Download, Calendar } from 'lucide-react';
import { format } from 'date-fns';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { downloadPdfReport } from '../lib/pdfReport';
import { DispatchedTab } from '../components/DispatchedTab';
import { ClientsTab } from '../components/ClientsTab';

const LEVEL_COLORS = { Light: '#fbbf24', Medium: '#f59e0b', Dark: '#44403c' };
const CAT_COLORS = ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#06b6d4', '#8b5cf6', '#f43f5e', '#14b8a6', '#eab308', '#3b82f6'];

const RoastedStockPage = () => {
  const [tab, setTab] = useState('stock'); // 'stock' | 'dispatched' | 'clients'
  const [summary, setSummary] = useState(null);
  const [stock, setStock] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snapshotDate, setSnapshotDate] = useState(new Date().toISOString().split('T')[0]);
  const [snapshot, setSnapshot] = useState(null);
  const [snapshotLoading, setSnapshotLoading] = useState(false);

  const fetchSnapshot = useCallback(async (dateStr) => {
    setSnapshotLoading(true);
    try {
      const iso = new Date(dateStr + 'T23:59:59Z').toISOString();
      const r = await roastedStock.getSnapshot(iso);
      setSnapshot(r.data);
    } catch (e) {
      console.error(e);
    } finally {
      setSnapshotLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const [s, st, c] = await Promise.all([
        roastedStock.getSummary(),
        roastedStock.getAll(),
        greenCategories.getAll(),
      ]);
      setSummary(s.data);
      setStock(st.data);
      setCats(c.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => { fetchSnapshot(snapshotDate); }, [fetchSnapshot, snapshotDate]);

  const handleStockChanged = () => {
    fetchAll();
    fetchSnapshot(snapshotDate);
  };

  const handleDownloadSnapshotPdf = () => {
    if (!snapshot) return;
    const asOf = format(new Date(snapshot.as_of_date), 'MMM dd, yyyy');
    const monthStart = format(new Date(snapshot.month_start), 'MMM dd, yyyy');
    downloadPdfReport({
      title: 'Roasted Stock Snapshot',
      subtitle: `As of ${asOf} · Month started ${monthStart}`,
      summary: [
        { label: 'Opening (Month Start)', value: `${snapshot.total_opening_kg.toFixed(2)} kg` },
        { label: '+ Roasted (Period)', value: `${snapshot.total_added_kg.toFixed(2)} kg` },
        { label: '− Dispatched (Period)', value: `${snapshot.total_dispatched_kg.toFixed(2)} kg` },
        { label: 'Closing (Selected Day)', value: `${snapshot.total_closing_kg.toFixed(2)} kg` },
      ],
      tables: [
        {
          title: 'By Roast Level',
          head: ['Roast Level', 'Opening (kg)', '+ Added (kg)', '− Dispatched (kg)', 'Closing (kg)'],
          rows: snapshot.by_roast_level.map((l) => [
            l.name,
            l.opening_kg.toFixed(2),
            `+${l.added_kg.toFixed(2)}`,
            `−${l.dispatched_kg.toFixed(2)}`,
            l.closing_kg.toFixed(2),
          ]),
        },
        {
          title: 'Per Category × Roast Level',
          head: ['Category', 'Roast Level', 'Opening', '+ Added', '− Dispatched', 'Closing'],
          rows: snapshot.rows.map((r) => [
            r.category_name,
            r.roast_level,
            r.opening_kg.toFixed(2),
            `+${r.added_kg.toFixed(2)}`,
            `−${r.dispatched_kg.toFixed(2)}`,
            r.closing_kg.toFixed(2),
          ]),
        },
      ],
      filename: `roasted-stock-snapshot_${snapshotDate}.pdf`,
    });
  };

  const handleDownloadStockPdf = () => {
    if (!summary) return;
    downloadPdfReport({
      title: 'Roasted Stock Report',
      subtitle: 'Current inventory snapshot',
      summary: [
        { label: 'Total Roasted Stock', value: `${summary.total_kg.toFixed(2)} kg` },
        ...summary.by_roast_level.map((l) => ({ label: `${l.name} Roast`, value: `${l.quantity_kg.toFixed(2)} kg` })),
        { label: 'Categories', value: summary.by_category.length },
      ],
      tables: [
        {
          title: 'Stock by Category & Roast Level',
          head: ['Category', 'Roast Level', 'Quantity (kg)'],
          rows: stock.map((s) => [s.category_name, s.roast_level, (s.quantity_kg || 0).toFixed(2)]),
        },
      ],
    });
  };

  if (loading || !summary) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roasted-stock-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Roasted Stock</h1>
          <p className="text-gray-600 mt-1">Track roasted inventory, dispatches and clients</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 flex gap-6 overflow-x-auto">
        <button
          onClick={() => setTab('stock')} data-testid="tab-stock"
          className={`pb-3 px-1 border-b-2 font-semibold text-sm transition whitespace-nowrap ${tab === 'stock' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <Coffee className="inline w-4 h-4 mr-2" />Stock
        </button>
        <button
          onClick={() => setTab('dispatched')} data-testid="tab-dispatched"
          className={`pb-3 px-1 border-b-2 font-semibold text-sm transition whitespace-nowrap ${tab === 'dispatched' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <Truck className="inline w-4 h-4 mr-2" />Dispatched
        </button>
        <button
          onClick={() => setTab('clients')} data-testid="tab-clients"
          className={`pb-3 px-1 border-b-2 font-semibold text-sm transition whitespace-nowrap ${tab === 'clients' ? 'border-amber-500 text-amber-600' : 'border-transparent text-gray-500 hover:text-gray-800'}`}>
          <Users className="inline w-4 h-4 mr-2" />Clients
        </button>
      </div>

      {tab === 'stock' && (
        <>
          {/* Snapshot — opening + as-of-date */}
          <div className="bg-gradient-to-br from-orange-600 to-amber-700 rounded-xl shadow-lg p-5 text-white" data-testid="roasted-snapshot-card">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
              <div>
                <h2 className="text-xl font-bold">Stock Snapshot</h2>
                <p className="text-white/80 text-sm">
                  {snapshot ? `Month started ${format(new Date(snapshot.month_start), 'MMM dd, yyyy')}` : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2 bg-white/15 rounded-lg px-3 py-2">
                  <Calendar className="w-4 h-4" />
                  <label className="text-sm">As of</label>
                  <input
                    type="date"
                    value={snapshotDate}
                    onChange={(e) => setSnapshotDate(e.target.value)}
                    data-testid="roasted-snapshot-date-input"
                    max={new Date().toISOString().split('T')[0]}
                    className="bg-transparent border-b border-white/30 text-white text-sm focus:outline-none focus:border-white"
                  />
                </div>
                <button
                  onClick={handleDownloadSnapshotPdf}
                  disabled={!snapshot}
                  data-testid="download-roasted-snapshot-pdf"
                  className="flex items-center gap-1.5 bg-white text-orange-700 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-orange-50 transition disabled:opacity-50"
                >
                  <Download className="w-4 h-4" />
                  <span>PDF</span>
                </button>
              </div>
            </div>
            {snapshotLoading || !snapshot ? (
              <p className="text-white/80 py-6 text-center text-sm">Loading snapshot…</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className="text-xs uppercase text-white/80">Opening (Month Start)</p>
                    <p className="text-xl font-bold mt-1">{snapshot.total_opening_kg.toFixed(2)} kg</p>
                  </div>
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className="text-xs uppercase text-white/80">+ Roasted (Period)</p>
                    <p className="text-xl font-bold mt-1 text-emerald-200">+{snapshot.total_added_kg.toFixed(2)} kg</p>
                  </div>
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className="text-xs uppercase text-white/80">− Dispatched (Period)</p>
                    <p className="text-xl font-bold mt-1 text-rose-200">−{snapshot.total_dispatched_kg.toFixed(2)} kg</p>
                  </div>
                  <div className="bg-white/15 rounded-lg p-3">
                    <p className="text-xs uppercase text-white/80">Closing (Selected Day)</p>
                    <p className="text-xl font-bold mt-1">{snapshot.total_closing_kg.toFixed(2)} kg</p>
                  </div>
                </div>
                {/* By Roast Level row */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  {snapshot.by_roast_level.map((lvl) => (
                    <div key={lvl.name} className="bg-white/10 rounded-lg p-3 border border-white/10">
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-semibold">{lvl.name} Roast</p>
                        <p className="text-lg font-bold">{lvl.closing_kg.toFixed(2)} kg</p>
                      </div>
                      <div className="text-xs text-white/80 space-y-0.5">
                        <p>Opening: {lvl.opening_kg.toFixed(2)} kg</p>
                        <p className="text-emerald-200">+ Added: {lvl.added_kg.toFixed(2)} kg</p>
                        <p className="text-rose-200">− Dispatched: {lvl.dispatched_kg.toFixed(2)} kg</p>
                      </div>
                    </div>
                  ))}
                </div>
                {/* Per (category, level) breakdown */}
                {snapshot.rows.length > 0 && (
                  <div className="bg-white/10 rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-white/10">
                          <tr>
                            <th className="text-left py-2 px-3 font-semibold">Category</th>
                            <th className="text-left py-2 px-3 font-semibold">Roast Level</th>
                            <th className="text-right py-2 px-3 font-semibold">Opening</th>
                            <th className="text-right py-2 px-3 font-semibold">+ Added</th>
                            <th className="text-right py-2 px-3 font-semibold">− Dispatched</th>
                            <th className="text-right py-2 px-3 font-semibold">Closing</th>
                          </tr>
                        </thead>
                        <tbody>
                          {snapshot.rows.map((r) => (
                            <tr key={`${r.category_name}-${r.roast_level}`} className="border-t border-white/10">
                              <td className="py-2 px-3">{r.category_name}</td>
                              <td className="py-2 px-3">{r.roast_level}</td>
                              <td className="py-2 px-3 text-right">{r.opening_kg.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-emerald-200">+{r.added_kg.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right text-rose-200">−{r.dispatched_kg.toFixed(2)}</td>
                              <td className="py-2 px-3 text-right font-bold">{r.closing_kg.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Total Inventory */}
          <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl shadow-lg p-6 text-white flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <p className="text-white/90 text-sm uppercase tracking-wide">Total Roasted Inventory</p>
              <p className="text-5xl font-bold mt-1">{summary.total_kg.toFixed(2)} <span className="text-2xl font-normal opacity-90">kg</span></p>
            </div>
            <button onClick={handleDownloadStockPdf} data-testid="download-stock-pdf"
              className="flex items-center space-x-2 bg-white text-amber-700 px-5 py-2.5 rounded-lg font-semibold hover:bg-amber-50 transition shadow">
              <Download className="w-4 h-4" /><span>Download PDF</span>
            </button>
          </div>

          {/* By Category Bar Chart */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-lg font-bold text-gray-800 mb-4">Stock by Category</h3>
            {summary.by_category.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No roasted stock yet.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={summary.by_category} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="name" angle={-15} textAnchor="end" height={50} tick={{ fontSize: 12 }} />
                  <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 12 }} />
                  <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg`, 'Stock']} />
                  <Bar dataKey="quantity_kg" radius={[6, 6, 0, 0]}>
                    {summary.by_category.map((entry, idx) => (
                      <Cell key={entry.name} fill={CAT_COLORS[idx % CAT_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Per Roast Level Sections */}
          {['Light', 'Medium', 'Dark'].map((level) => {
            const catBreakdown = summary.by_roast_level_category[level] || [];
            const levelTotal = (summary.by_roast_level.find((l) => l.name === level) || {}).quantity_kg || 0;
            return (
              <div key={level} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`w-4 h-4 rounded-full`} style={{ backgroundColor: LEVEL_COLORS[level] }}></div>
                    <h3 className="text-lg font-bold text-gray-800">{level} Roast</h3>
                  </div>
                  <p className="text-2xl font-bold" style={{ color: LEVEL_COLORS[level] }}>
                    {levelTotal.toFixed(2)} <span className="text-sm font-normal">kg</span>
                  </p>
                </div>
                {catBreakdown.length === 0 ? (
                  <p className="text-center text-gray-400 py-6 text-sm">No {level.toLowerCase()} roast stock.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={catBreakdown} margin={{ top: 10, right: 20, left: 0, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="name" angle={-15} textAnchor="end" height={50} tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `${v}kg`} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => [`${Number(v).toFixed(2)} kg`, 'Stock']} />
                      <Bar dataKey="quantity_kg" fill={LEVEL_COLORS[level]} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            );
          })}
        </>
      )}

      {tab === 'dispatched' && (
        <DispatchedTab stock={stock} cats={cats} onStockChanged={handleStockChanged} />
      )}

      {tab === 'clients' && <ClientsTab />}
    </div>
  );
};

export default RoastedStockPage;
